/**
 * Copyright (C) 2026 Jugendstil, SLU - All Rights Reserved
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited. Proprietary and confidential.
 */
import { paintAll, register } from '../registry.js';

/*
 * The bottom sheet.
 *
 * `showModal()` and nothing else: the dialog element already does the backdrop, the focus trap,
 * Escape to dismiss, returning focus to the opener and making the rest of the page inert.
 * Re-implementing any of that would be worse than what the platform ships.
 */

/**
 * A sheet the page arrived with already open.
 *
 * A server-rendered form that came back with errors is the case this exists for: the sheet it was
 * filled in is part of the page, and a page that returns the errors with the sheet shut is a page
 * refusing to explain itself. `showModal()` rather than the `open` attribute, because an `open`
 * dialog is not a modal one - no backdrop, not in the top layer, and the page behind it still
 * scrolls.
 */
export function openOnLoad(sheet: HTMLDialogElement): void {
    if (!sheet.open) {
        sheet.showModal();
    }
}

/**
 * Stops a sheet handing focus to the first thing in it, which is the button that dismisses it.
 *
 * `showModal()` focuses the first focusable descendant, and that is Cancel in every sheet here:
 * each one opened with a ring around its dismiss button, as though leaving were the thing to do
 * next. The panel takes the focus instead, so a reader still lands inside the sheet and tabs
 * forward through its contents with nothing singled out.
 *
 * `autofocus` on the dialog does not do this - the attribute is only honoured on a descendant, so
 * the dialog has to be focused by hand, which means knowing when one opened. A dialog fires no
 * event for that, so `watchForOpening` watches the attribute that says so.
 */
export function focusWholeSheet(sheet: HTMLDialogElement): void {
    sheet.setAttribute('tabindex', '-1');
}

/** How far down the sheet has to be pulled before releasing it dismisses rather than snaps back. */
const DISMISS_DISTANCE = 120;

/** Or how fast, in pixels per millisecond, so a flick dismisses as readily as a long slow pull. */
const DISMISS_VELOCITY = 0.5;

/**
 * How long after the last movement a release still counts as a flick.
 *
 * Past it the finger was at rest when it let go, whatever it had been doing before, and a panel
 * held still at 100px has not been thrown anywhere.
 */
const FLICK_WINDOW = 100;

interface Drag {
    readonly pointerId: number;
    readonly sheet: HTMLDialogElement;
    readonly startY: number;
    lastAt: number;
    lastY: number;
    offset: number;
    velocity: number;
}

let drag: Drag | undefined;

/**
 * Whether a gesture starting here should pull the sheet or be left to what is under the finger.
 *
 * Anything scrollable that is not already at its top scrolls: pulling the panel instead would make
 * a list impossible to scroll back up. At the top there is nowhere further to scroll, so the
 * gesture belongs to the sheet - which is the rule iOS itself uses, and the reason a sheet feels
 * continuous with the list inside it rather than in competition with it.
 *
 * `data-wp-sheet-no-drag` is for a region that answers a drag itself and is not a control, a
 * scroller or anything else this can recognise: an image cropper, where pulling the crop box down
 * is the whole of what it is for, and where every pull would otherwise dismiss the sheet instead
 * of moving the box. Marked by the region rather than inferred, because nothing about such an
 * element says so from the outside.
 */
function shouldDrag(target: Element, sheet: HTMLDialogElement): boolean {
    for (let node: Element | null = target; node !== null; node = node.parentElement) {
        if (node.scrollTop > 0) {
            return false;
        }

        if (node === sheet) {
            break;
        }
    }

    if (target.closest('[data-wp-sheet-no-drag]') !== null) {
        return false;
    }

    /* A control is for pressing, not for dragging the panel it is sitting on. */
    return target.closest('a, button, input, label, select, textarea') === null;
}

/**
 * Lets go of the sheet, either closing it or putting it back.
 *
 * The inline offset goes before the close in both cases and the dragging flag goes before that,
 * which is the whole of why the release looks right: clearing the flag restores the transition,
 * and clearing the offset in the same frame leaves the browser animating from wherever the finger
 * left the panel - down and out when it is closing, back up to the detent when it is not. Clearing
 * them in the other order would snap first and animate from nowhere.
 */
function endDrag(dismiss: boolean): void {
    if (drag === undefined) {
        return;
    }

    const { sheet } = drag;
    drag = undefined;

    sheet.removeAttribute('data-wp-sheet-dragging');
    sheet.style.removeProperty('translate');

    if (dismiss) {
        sheet.close();
    }
}

/**
 * Pull down to dismiss, as a sheet does on iOS.
 *
 * Pointer events rather than touch events: one set of handlers covers a finger, a trackpad and a
 * mouse, and `setPointerCapture` keeps the gesture alive once the pointer leaves the panel - which
 * it does immediately and constantly, because pulling a sheet down means pulling it out from under
 * the pointer.
 *
 * Downwards only. A sheet here has one height, so there is nothing above the open position to drag
 * towards, and following the finger upwards would promise a taller sheet that does not exist.
 *
 * On the document rather than on each sheet, for the same reason the opening and closing clicks
 * are: a sheet can arrive with a Turbo frame at any point, and a listener that has to be attached
 * to it is a listener some sheet will be missing.
 */
export function listenForDragging(): void {
    document.addEventListener(
        'pointerdown',
        (event) => {
            const target = event.target;
            if (!(target instanceof Element) || event.button !== 0) {
                return;
            }

            const sheet = target.closest<HTMLDialogElement>('.wp-sheet[open]');
            if (sheet === null || !shouldDrag(target, sheet)) {
                return;
            }

            drag = {
                lastAt: event.timeStamp,
                lastY: event.clientY,
                offset: 0,
                pointerId: event.pointerId,
                sheet,
                startY: event.clientY,
                velocity: 0
            };
            sheet.setPointerCapture?.(event.pointerId);
        },
        { passive: true }
    );

    document.addEventListener('pointermove', (event) => {
        if (drag === undefined || event.pointerId !== drag.pointerId) {
            return;
        }

        drag.offset = Math.max(0, event.clientY - drag.startY);

        /* Speed over the last move alone, not over the gesture so far. Averaged from the start, a
           flick at the end of a slow pull reads as slow - and a drag held still for a second
           before release reads as whatever it was doing a second ago. */
        drag.velocity = (event.clientY - drag.lastY) / Math.max(1, event.timeStamp - drag.lastAt);
        drag.lastAt = event.timeStamp;
        drag.lastY = event.clientY;

        drag.sheet.setAttribute('data-wp-sheet-dragging', '');
        drag.sheet.style.setProperty('translate', `0 ${drag.offset}px`);
    });

    document.addEventListener('pointerup', (event) => {
        if (drag === undefined || event.pointerId !== drag.pointerId) {
            return;
        }

        /* Distance or speed, because the two describe the same intention at different lengths: a
           deliberate pull most of the way down, and a flick that says the same thing in 80ms. */
        const flicked = event.timeStamp - drag.lastAt < FLICK_WINDOW && drag.velocity >= DISMISS_VELOCITY;

        endDrag(drag.offset >= DISMISS_DISTANCE || flicked);
    });

    /* A cancel is the browser taking the gesture over, not the reader finishing one, so it always
       puts the sheet back however far down it had been pulled. */
    document.addEventListener('pointercancel', (event) => {
        if (drag !== undefined && event.pointerId === drag.pointerId) {
            endDrag(false);
        }
    });
}

export function paint(root: ParentNode): void {
    root.querySelectorAll<HTMLDialogElement>('.wp-sheet').forEach(focusWholeSheet);
    root.querySelectorAll<HTMLDialogElement>('.wp-sheet[data-wp-sheet-open-on-load]').forEach(openOnLoad);
}

/**
 * Watching rather than focusing at each call site, because a sheet has to behave the same however
 * it was opened: by the library's own opener, by a page that arrives with one open, or by a
 * controller calling `showModal()` itself, which is how the result form opens all six of its
 * pickers.
 */
export function watchForOpening(): MutationObserver {
    const observer = new MutationObserver((records) => {
        records.forEach((record) => {
            const target = record.target;
            if (target instanceof HTMLDialogElement && target.matches('.wp-sheet') && target.open) {
                target.focus({ preventScroll: true });
            }
        });
    });

    observer.observe(document.documentElement, { attributeFilter: ['open'], subtree: true });

    return observer;
}

export function listen(): void {
    /*
     * Clicking the backdrop closes it. A <dialog>'s own box covers the whole viewport, so a click
     * on the dim area still has the dialog as its target; comparing the target to the element
     * itself is how you tell "outside the panel" from "inside it".
     */
    document.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const opener = target.closest('[data-wp-sheet-open]');
        if (opener !== null) {
            event.preventDefault();

            const id = opener.getAttribute('data-wp-sheet-open') ?? '';
            const sheet = document.getElementById(id);
            if (sheet instanceof HTMLDialogElement) {
                sheet.showModal();

                /* Nothing inside a closed dialog has a layout, so anything in here that has to be
                   put in a position - a wheel scrolled to its value - could not be while the sheet
                   was shut, and a scroller that is reshown starts at the top again. Painting on
                   open is what makes those components right every time the sheet is opened rather
                   than only the first. */
                paintAll(sheet);
            }

            return;
        }

        const closer = target.closest('[data-wp-sheet-close]');
        if (closer !== null) {
            event.preventDefault();
            closer.closest('dialog')?.close();

            return;
        }

        if (target instanceof HTMLDialogElement && target.matches('.wp-sheet')) {
            target.close();
        }
    });

    watchForOpening();
    listenForDragging();
    register(paint);
}
